import React, { useState } from "react";

import { cn } from "../../../common/u1/cn";
import { CountrySelect } from "../../../common/u1/CountrySelect";
import { Input } from "../../../common/u1/Input";
import { getCountryByCode } from "../../../../utils/Helper";
import { AlertIcon, SearchIcon, BuildingIcon } from "../../../../assets/ui/icons";
import { EditDialog } from "./EditDialog";
import { DialogBreadcrumb } from "./DialogBreadcrumb";
import { useOwnershipSearch } from "./useOwnershipSearch";
import {
  EMPTY_STATE_TEXT,
  emptyStateFor,
  resultCountryCode,
  resultName,
  resultState,
  searchWarning,
} from "./ownershipSearch";

/* Step 2 of adding a corporate owner — pick the business. */

const STATE_DOT = {
  verified: "bg-[var(--success)]",
  claimed: "bg-[var(--navy)]",
  listed: "bg-[var(--muted)]",
};

export const OwnershipSearchModal = ({
  onClose,
  onBack,
  onSelect,
  onNotFound,
}) => {
  const { term, setTerm, country, setCountry, results, isLoading, error, message } =
    useOwnershipSearch();
  const [chosen, setChosen] = useState(null);

  const emptyState = emptyStateFor({ term, results, isLoading, error });
  const warning = searchWarning(message);

  return (
    <EditDialog
      title=""
      onClose={onClose}
      ctaLabel="Select"
      /* Transcribed: Select returns to the form. It is only meaningful once a
         business has been picked, so it waits for one rather than returning an
         empty selection. */
      onSubmit={() => chosen && onSelect?.(chosen)}
      submitDisabled={!chosen}
      width={540}
    >
      <DialogBreadcrumb
        prev="Add Ownership Business"
        next="Select Business"
        onBack={onBack}
      />

      {/* The same searchable picker as everywhere else, so this step and the
          form behind it behave alike. */}
      <CountrySelect
        className="mt-4"
        label="Select Country"
        labelVariant="field"
        id="u1-owner-search-country"
        value={country}
        onChange={(c) => setCountry(c?.code)}
        placeholder="Select Country"
      />

      <Input
        wrapperClassName="mt-3.5"
        label="Search your Business"
        id="u1-owner-search-term"
        type="search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search"
        hint={
          /* Said up front rather than after a fruitless keystroke. */
          term && term.trim().length < 3 ? "Type at least 3 characters." : undefined
        }
      />

      {warning && (
        <div
          role="status"
          className={cn(
            "mt-2.5 flex items-start gap-2 rounded-lg border px-3 py-2",
            "border-[var(--gold-border)] bg-[var(--gold-soft)]",
            "text-[11.5px] leading-[1.45] text-[var(--gold-deep)] font-sans"
          )}
        >
          <span aria-hidden="true" className="mt-[1px] shrink-0">
            <AlertIcon size={13} />
          </span>
          <span>{warning}</span>
        </div>
      )}

      {isLoading && (
        <p
          role="status"
          aria-live="polite"
          className="mt-3 text-[12.5px] text-[var(--muted)] font-sans"
        >
          Searching…
        </p>
      )}

      {emptyState && (
        <div className="mt-4 flex flex-col items-center gap-2 py-6 text-center">
          <span
            aria-hidden="true"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--bg)] text-[var(--muted-2)]"
          >
            <SearchIcon size={20} />
          </span>
          <p className="m-0 text-[13px] text-[var(--muted)] font-sans">
            {EMPTY_STATE_TEXT[emptyState]}
          </p>
        </div>
      )}

      {results.length > 0 && (
        <ul
          aria-label="Search results"
          className="m-0 mt-3 flex max-h-[220px] list-none flex-col gap-2 overflow-y-auto p-0"
        >
          {results.map((company, index) => {
            const name = resultName(company);
            const picked = chosen === company;
            return (
              <li key={`${name}-${index}`}>
                <button
                  type="button"
                  aria-pressed={picked}
                  onClick={() => setChosen(company)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-[10px] border px-3 py-2.5 text-left",
                    "transition-colors focus-visible:outline-none focus-visible:ring-2",
                    "focus-visible:ring-[var(--coral)]",
                    picked
                      ? "border-[var(--coral)] bg-[var(--coral-soft)]"
                      : "border-[var(--line)] bg-white hover:border-[var(--line-2)]"
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[var(--line)] bg-[var(--bg)] text-[var(--muted-2)]"
                  >
                    <BuildingIcon size={16} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-[var(--ink)] font-sans">
                      {name}
                    </span>
                    <span className="block truncate text-[11.5px] text-[var(--muted)] font-sans">
                      {company?.state ? `${company.state}, ` : ""}
                      {getCountryByCode(resultCountryCode(company))}
                    </span>
                  </span>
                  {/* Transcribed from SearchItem's status dot. */}
                  <span
                    aria-hidden="true"
                    className={cn(
                      "h-2.5 w-2.5 shrink-0 rounded-full",
                      STATE_DOT[resultState(company)]
                    )}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <p className="m-0 mt-4 text-[12.5px] text-[var(--muted)] font-sans">
        Business not found?{" "}
        <button
          type="button"
          onClick={onNotFound}
          className="font-bold text-[var(--coral)] underline decoration-[1.5px] underline-offset-2 hover:text-[var(--coral-deep)]"
        >
          Click here
        </button>
      </p>
    </EditDialog>
  );
};

export default OwnershipSearchModal;
